import type { Event, EventMoment } from "@/services/api/events";

/**
 * Format a date string to a long format (e.g., "January 15, 2025")
 */
export function formatDate(dateString: string | null | undefined, locale: string): string {
  if (!dateString) return ''; // JUB-154: date not decided yet
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'ro' ? 'ro-RO' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date string to a short format (e.g., "Jan 15, 2025")
 */
export function formatDateShort(dateString: string | null | undefined, locale: string): string {
  if (!dateString) return ''; // JUB-154: date not decided yet
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'ro' ? 'ro-RO' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * ISO 3166-1 alpha-3 -> alpha-2 mapping for the countries the platform serves.
 * Events store the country as alpha-3 (e.g. "ROU"); Intl.DisplayNames only
 * accepts alpha-2 region subtags.
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  ROU: 'RO', MDA: 'MD', HUN: 'HU', BGR: 'BG', SRB: 'RS', UKR: 'UA',
  DEU: 'DE', FRA: 'FR', ITA: 'IT', ESP: 'ES', PRT: 'PT', GBR: 'GB',
  IRL: 'IE', AUT: 'AT', BEL: 'BE', NLD: 'NL', CHE: 'CH', GRC: 'GR',
  POL: 'PL', CZE: 'CZ', SVK: 'SK', SWE: 'SE', NOR: 'NO', DNK: 'DK',
  FIN: 'FI', USA: 'US', CAN: 'CA', AUS: 'AU',
};

/**
 * Resolve a stored country code (ISO alpha-3 like "ROU", legacy alpha-2 like
 * "ro") to a localized display name ("România" / "Romania"). Returns an empty
 * string when the code cannot be resolved, so callers can simply omit it
 * instead of rendering the raw code (JUB-62).
 */
export function getCountryDisplayName(code: string | null | undefined, locale: string): string {
  if (!code) return '';
  const raw = code.trim().toUpperCase();
  const alpha2 = raw.length === 3 ? ALPHA3_TO_ALPHA2[raw] : raw.length === 2 ? raw : undefined;
  if (!alpha2) return '';
  try {
    return new Intl.DisplayNames([locale === 'ro' ? 'ro' : 'en'], { type: 'region' }).of(alpha2) ?? '';
  } catch {
    return '';
  }
}

/**
 * Turn literal backslash-n sequences into real newlines (JUB-99). Free-text
 * fields imported or seeded with single-quoted '\n' otherwise render the two
 * characters verbatim in prose; renderers pair this with whitespace-pre-line.
 */
export function normalizeMultilineText(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/\\n/g, '\n');
}

/**
 * Get the display title for an event
 * The saved event name is authoritative; couple names are a legacy fallback.
 */
export function getEventTitle(event: Event): string {
  if (event.name?.trim()) {
    return event.name;
  }
  if (event.starBride && event.starGroom) {
    return `${event.starBride} & ${event.starGroom}`;
  }
  return event.name || '';
}

/**
 * Get event statistics
 * TODO: Replace with real API data
 */
export type BankLinkStatus = 'linked' | 'action_required' | 'pending' | 'not_linked';

export function getEventStats(
  event: Event,
  overrides?: Partial<{
    plan: 'free' | 'standard' | 'premium';
    bankLinkStatus: BankLinkStatus;
    stripeAccountConnected: boolean;
  }>
) {
  // These should come from the API, but for now we'll use placeholders
  const plan = overrides?.plan ?? 'free';
  const bankLinkStatus = overrides?.bankLinkStatus ?? 'not_linked';
  const bankLinked = bankLinkStatus === 'linked';
  const stripeAccountConnected = overrides?.stripeAccountConnected ?? false;
  const confirmedGuests = event.stats?.confirmedGuests ?? 0;
  const totalGifts = event.stats?.totalGifts ?? 0;
  const totalContributions = event.stats?.totalContributions ?? 0;

  return {
    confirmedGuests,
    totalGifts,
    totalContributions,
    plan,
    bankLinked,
    bankLinkStatus,
    stripeAccountConnected,
  };
}

/**
 * JUB-169: what the event celebrates, from the flags the editor and the public
 * templates already use (includesWedding defaults to true, includesBaptism to
 * false). Legacy baptism rows only have type 1.
 */
export type EventKind = 'wedding' | 'baptism' | 'weddingBaptism';

export function getEventKind(event: Pick<Event, 'type' | 'includesWedding' | 'includesBaptism'>): EventKind {
  if (event.includesWedding === false || (event.type === 1 && event.includesWedding !== true)) return 'baptism';
  return event.includesBaptism ? 'weddingBaptism' : 'wedding';
}

/** JUB-169: i18n key of the card's event type label ("Nuntă", "Botez", "Nuntă și botez"). */
export function getEventKindLabelKey(event: Pick<Event, 'type' | 'includesWedding' | 'includesBaptism'>): string {
  return `events.types.${getEventKind(event)}`;
}

/**
 * JUB-169: the labels of the event card's moment chips — only the moments the
 * owner enabled, in the order the API sends them (civil, religious, baptism,
 * party). A stored custom title wins ("Cununia religioasă și Botezul");
 * otherwise the localized default for the slot. A baptism-only event keeps its
 * baptism ceremony in the religious slot, so that slot defaults to "Botezul".
 * No moments means no chips: nothing is ever invented.
 */
export function getEventMomentLabels(
  event: Pick<Event, 'moments' | 'type' | 'includesWedding' | 'includesBaptism'>,
  t: (key: string) => string
): string[] {
  const baptismOnly = getEventKind(event) === 'baptism';
  return (event.moments ?? []).map((moment: EventMoment) => {
    const title = moment.title?.trim();
    if (title) return title;
    if (moment.type === 'religious' && baptismOnly) return t('onboarding.moments.baptism');
    return t(`onboarding.moments.${moment.type}`);
  });
}

/**
 * Usage meter for the plan's confirmed-guest cap ("x/50", JUB-75).
 *
 * The number that matters is CONFIRMED guests, not invited guests or raw RSVP
 * responses: that is what the plan cap actually gates. The public RSVP submit
 * path (server/app/Http/Controllers/PublicPages/RsvpController.php) enforces
 * the cap against `Guest::confirmedPeople()`, and `EventResource` exposes the
 * exact same count as `event.stats.confirmedGuests` — so this reuses that
 * number rather than deriving a different one on the frontend.
 *
 * `guestLimit` comes from the plan's `limits.guests` (free 50, standard 200);
 * -1 means unlimited (Premium) and has nothing to meter, so callers get
 * `null` and should render no meter.
 */
export interface GuestUsageMeter {
  used: number;
  limit: number;
  /** 0-100, clamped — a downgrade can leave `used` above `limit`. */
  percent: number;
  atLimit: boolean;
  nearLimit: boolean;
}

export function getGuestUsageMeter(
  confirmedGuests: number | null | undefined,
  guestLimit: number | null | undefined
): GuestUsageMeter | null {
  if (guestLimit === null || guestLimit === undefined || guestLimit <= 0) {
    return null;
  }

  const used = Math.max(0, confirmedGuests ?? 0);
  const percent = Math.min(100, Math.round((used / guestLimit) * 100));

  return {
    used,
    limit: guestLimit,
    percent,
    atLimit: used >= guestLimit,
    nearLimit: used >= guestLimit * 0.9,
  };
}
